// useFocusTrap — keyboard focus trap for modal dialogs.
//
// Matches the chat design brief's "Focus trap in all dialogs; Escape
// closes" rule. The trap:
//   - Moves focus to the first focusable element inside `ref` on mount
//     (or to `ref` itself when nothing inside is tabbable).
//   - Cycles Tab / Shift+Tab within the dialog.
//   - Restores focus to the previously-active element on unmount.
//
// Escape handling stays with the dialog — different dialogs want
// different dismiss semantics (save-flow ignores Escape; verification
// cancels the session). Keeping Escape outside the hook avoids
// forcing every dialog into one policy.

import { useEffect, type RefObject } from "react";

const trapStack: RefObject<HTMLElement | null>[] = [];

/** All natively-focusable elements the browser will Tab through,
 *  minus disabled / negative-tabindex entries which participate in
 *  neither keyboard flow. */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

interface VisibilityCheckOptions {
  visibilityProperty?: boolean;
  opacityProperty?: boolean;
  checkVisibilityCSS?: boolean;
  checkOpacity?: boolean;
}

function isVisible(el: HTMLElement): boolean {
  if (typeof el.checkVisibility === "function") {
    const options: VisibilityCheckOptions = {
      visibilityProperty: true,
      opacityProperty: true,
      checkVisibilityCSS: true,
      checkOpacity: true,
    };
    return el.checkVisibility(options);
  }
  return el.getClientRects().length > 0;
}

function focusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute("disabled") && isVisible(el),
  );
}

function focusFirstIn(container: HTMLElement): void {
  const first = focusable(container)[0];
  if (first) {
    first.focus();
  } else {
    container.setAttribute("tabindex", "-1");
    container.focus();
  }
}

function addTrap(ref: RefObject<HTMLElement | null>, container: HTMLElement): boolean {
  let nestedTrapIndex = -1;
  for (let index = 0; index < trapStack.length; index += 1) {
    const trapRef = trapStack[index];
    if (!trapRef) continue;
    const trapContainer = trapRef.current;
    if (
      trapContainer !== null &&
      trapContainer !== container &&
      container.contains(trapContainer)
    ) {
      nestedTrapIndex = index;
      break;
    }
  }
  if (nestedTrapIndex === -1) {
    trapStack.push(ref);
    return true;
  }
  trapStack.splice(nestedTrapIndex, 0, ref);
  return false;
}

function restoreFocus(previousFocus: HTMLElement | null): void {
  const nextTopContainer = trapStack[trapStack.length - 1]?.current;
  if (
    previousFocus &&
    document.contains(previousFocus) &&
    (!nextTopContainer || nextTopContainer.contains(previousFocus))
  ) {
    previousFocus.focus();
  } else if (nextTopContainer) {
    focusFirstIn(nextTopContainer);
  }
}

export function useFocusTrap(ref: RefObject<HTMLElement | null>, active = true): void {
  useEffect(() => {
    if (!active) return;
    const container = ref.current;
    if (!container) return;
    const previousFocus = document.activeElement as HTMLElement | null;

    const shouldSeedFocus = addTrap(ref, container);
    if (shouldSeedFocus) focusFirstIn(container);

    function onKey(e: KeyboardEvent) {
      if (e.key !== "Tab") return;
      if (trapStack[trapStack.length - 1] !== ref) return;
      const els = focusable(container!);
      if (els.length === 0) {
        e.preventDefault();
        return;
      }
      const firstEl = els[0];
      const lastEl = els[els.length - 1];
      if (!firstEl || !lastEl) return;
      const activeEl = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (activeEl === firstEl || !container!.contains(activeEl)) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (activeEl === lastEl || !container!.contains(activeEl)) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const wasOnTop = trapStack[trapStack.length - 1] === ref;
      const index = trapStack.lastIndexOf(ref);
      if (index !== -1) trapStack.splice(index, 1);
      if (!wasOnTop) {
        queueMicrotask(() => {
          const hasActiveDescendant = trapStack.some((trapRef) => {
            const trapContainer = trapRef.current;
            return trapContainer !== null && container.contains(trapContainer);
          });
          if (!hasActiveDescendant) restoreFocus(previousFocus);
        });
        return;
      }
      restoreFocus(previousFocus);
    };
  }, [ref, active]);
}
